import { useState, useEffect, useRef } from 'react';
import { listShardHealth, ShardHealthSnapshot } from '../api/admin-client';

const REFRESH_INTERVAL_MS = 30_000;

function heatClass(v: number): string {
  if (v < 40) return 'status-ok';
  if (v < 70) return 'status-warn';
  return 'status-error';
}

function errorRateClass(v: number): string {
  if (v === 0) return 'status-ok';
  if (v < 0.05) return 'status-warn';
  return 'status-error';
}

function snapshotAge(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return '<1 min';
  if (mins < 60) return `${mins} min`;
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
}

function lastEventAge(iso: string | null): string {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const secs = Math.floor(diff / 1000);
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m`;
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
}

function fmtCash(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n}`;
}

// ─── Simple error toast ───────────────────────────────────────────────────────

function ErrorToast({ message, onDismiss }: { message: string; onDismiss: () => void }) {
  // Auto-dismiss after 6 seconds
  useEffect(() => {
    const t = setTimeout(onDismiss, 6_000);
    return () => clearTimeout(t);
  }, [onDismiss]);

  return (
    <div className="error-toast">
      <span>{message}</span>
      <button
        className="btn btn-sm btn-ghost"
        onClick={onDismiss}
        style={{ padding: '2px 8px', fontSize: 11, marginLeft: 12 }}
      >
        Dismiss
      </button>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function ShardHealth() {
  const [snapshots, setSnapshots] = useState<ShardHealthSnapshot[]>([]);
  const [loading, setLoading] = useState(false);
  const [toastError, setToastError] = useState('');
  const [endpointMissing, setEndpointMissing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  async function load() {
    if (loading) return;
    setLoading(true);
    try {
      const data = await listShardHealth();
      setSnapshots(data);
      setLastRefreshed(new Date());
      setEndpointMissing(false);
      setToastError('');
    } catch (err: any) {
      if (err?.status === 404 || err?.status === 501 || err?.code === 'not_found') {
        setEndpointMissing(true);
      } else {
        // Show a toast error — don't wipe existing data from last successful fetch
        setToastError(err?.message ?? 'Failed to refresh shard health');
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (autoRefresh) {
      intervalRef.current = setInterval(load, REFRESH_INTERVAL_MS);
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [autoRefresh]);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 8 }}>
        <div className="page-title" style={{ marginBottom: 0 }}>Shard Health Dashboard</div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', marginLeft: 'auto' }}>
          <input
            type="checkbox"
            checked={autoRefresh}
            onChange={e => setAutoRefresh(e.target.checked)}
            style={{ width: 'auto' }}
          />
          <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Auto-refresh (30s)</span>
        </label>
        <button className="btn btn-ghost btn-sm" onClick={load} disabled={loading} title="Refresh now">
          {loading ? <span className="spinner" /> : '↻'} Refresh
        </button>
      </div>

      {lastRefreshed && (
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 16 }}>
          Last refreshed: {lastRefreshed.toLocaleTimeString()}
          {autoRefresh && <span style={{ marginLeft: 8 }}>· auto-refresh every 30s</span>}
        </div>
      )}

      {/* Toast error (non-destructive: keeps last good data visible) */}
      {toastError && (
        <ErrorToast
          message={`Refresh failed: ${toastError}`}
          onDismiss={() => setToastError('')}
        />
      )}

      {/* Backend stub banner */}
      {endpointMissing && (
        <div className="stub-banner">
          Endpoint <code>GET /admin/shard-health</code> is not yet exposed via HTTP.
          Backend ticket: Sprint 24 — expose <code>listAllShardHealth()</code> from shard-health-service
          over the admin HTTP layer.
          <br /><br />
          {snapshots.length > 0
            ? 'Showing data from the last successful fetch below.'
            : 'No previous data available.'}
        </div>
      )}

      {!loading && !endpointMissing && !toastError && snapshots.length === 0 && (
        <div className="empty-state">No shard health snapshots available.</div>
      )}

      {snapshots.length > 0 && (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Shard ID</th>
                <th>Players (24h)</th>
                <th>Gangs</th>
                <th>Last Event Age</th>
                <th>p50 Cash</th>
                <th>p50 Crew</th>
                <th>p95 Heat</th>
                <th>Error Rate (60s)</th>
                <th>Snapshot Age</th>
              </tr>
            </thead>
            <tbody>
              {snapshots.map(s => (
                <tr key={s.shard_id}>
                  <td className="text-mono">{s.shard_id}</td>
                  <td>{s.active_player_count}</td>
                  <td>{s.gang_count}</td>
                  <td className="text-muted">{lastEventAge(s.last_event_at)}</td>
                  <td>{fmtCash(s.p50_cash)}</td>
                  <td>{s.p50_crew}</td>
                  <td className={heatClass(s.p95_heat)}>{s.p95_heat.toFixed(1)}</td>
                  <td className={errorRateClass(s.error_rate_60s)}>{s.error_rate_60s.toFixed(3)}</td>
                  <td className="text-muted">{snapshotAge(s.snapshot_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <style>{`
        .error-toast {
          position: fixed;
          bottom: 24px;
          right: 24px;
          background: var(--bg-surface);
          border: 1px solid var(--danger);
          border-radius: var(--radius-md);
          color: var(--danger);
          padding: 12px 16px;
          font-size: 13px;
          display: flex;
          align-items: center;
          box-shadow: 0 8px 24px rgba(0,0,0,0.4);
          z-index: 500;
          animation: slideUp 0.25s ease-out;
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
